import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { SharedModule } from 'src/app/shared/shared.module';
import { getWidgetByTagName, ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { PropertyDef, WidgetPropertyType } from 'src/app/components/template-widgets/sample-widgets/widget-config.helpers';
import { TemplatePrintOptions, normalizeLegacyMargins } from 'src/app/models/reportTemplate.model';

export interface WidgetPropertyChange {
  widgetTag: string;
  properties: Record<string, boolean | string | number>;
}

@Component({
    standalone: true,
    selector: 'app-widget-property-panel',
    imports: [FormsModule, SharedModule],
    templateUrl: './widget-property-panel.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-property-panel.component.scss']
})
export class WidgetPropertyPanelComponent implements OnChanges {
  @Input() selectedWidgetTag: string | null = null;
  @Input() widgetProperties: Record<string, boolean | string | number> = {};
  @Input() printOptions: TemplatePrintOptions | null = null;
  @Input() entityType: string | null = null;
  @Output() propertiesChanged = new EventEmitter<WidgetPropertyChange>();
  @Output() printOptionsChanged = new EventEmitter<TemplatePrintOptions>();

  readonly WidgetPropertyType = WidgetPropertyType;

  readonly marginsMap: Record<string, string> = {
    'None': '0mm',
    'Micro': '2.5mm',
    'Small': '5mm',
    'Medium': '7.5mm',
    'Large': '10mm'
  };

  readonly marginsMmToLabel: Record<string, string> = {
    '0mm': 'None',
    '2.5mm': 'Micro',
    '5mm': 'Small',
    '7.5mm': 'Medium',
    '10mm': 'Large'
  };

  propertyDefs: PropertyDef[] = [];
  localProperties: Record<string, boolean | string | number> = {};
  localPrintOptions: TemplatePrintOptions | null = null;
  localMarginsLabel = 'None';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedWidgetTag'] || changes['widgetProperties']) {
      this.loadWidgetConfig();
    }
    if (changes['printOptions']) {
      if (this.printOptions) {
        this.localPrintOptions = {
          showPageNumbers: false,
          showGenerationDate: false,
          watermarkText: '',
          headerText: '',
          footerText: '',
          ...this.printOptions,
          margins: normalizeLegacyMargins(this.printOptions.margins)
        };
        this.localMarginsLabel = this.marginsMmToLabel[this.localPrintOptions.margins ?? '0mm'] ?? 'None';
      } else {
        this.localPrintOptions = null;
        this.localMarginsLabel = 'None';
      }
    }
  }

  private loadWidgetConfig(): void {
    if (!this.selectedWidgetTag) {
      this.propertyDefs = [];
      this.localProperties = {};
      return;
    }

    const widget = getWidgetByTagName(this.selectedWidgetTag);
    this.propertyDefs = widget?.editorPropertySchema?.propertySchema ?? [];
    this.localProperties = { ...this.widgetProperties };
  }

  onPropertyChange(): void {
    if (!this.selectedWidgetTag) return;
    this.propertiesChanged.emit({
      widgetTag: this.selectedWidgetTag,
      properties: { ...this.localProperties }
    });
  }

  onPrintOptionsChange(): void {
    if (this.localPrintOptions) {
      this.printOptionsChanged.emit({ ...this.localPrintOptions });
    }
  }

  onMarginsLabelChange(): void {
    if (!this.localPrintOptions) return;
    this.localPrintOptions.margins = this.marginsMap[this.localMarginsLabel] ?? '0mm';
    this.printOptionsChanged.emit({ ...this.localPrintOptions });
  }

  get hasWidget(): boolean {
    return !!this.selectedWidgetTag && this.propertyDefs.length > 0;
  }

  get hasPrintOptions(): boolean {
    return !!this.localPrintOptions;
  }

  get isRiskRegister(): boolean {
    return this.entityType === ENTITY_TYPES.RISK_REGISTER;
  }

  trackByKey(_: number, def: PropertyDef): string {
    return def.key;
  }
}
